'use client';

import Link from 'next/link';
import { Compass } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

/**
 * Root not-found boundary -- the fallback for any URL that matches no route,
 * and for any notFound() thrown by a segment with no nearer boundary.
 *
 * Without this file Next.js serves its own built-in 404: unstyled, outside the
 * app shell, English-only on a bilingual site, and offering no way back. A
 * truncated or mistyped link is the ordinary way a reader reaches it (#1429),
 * so the dead end had to become a signpost.
 *
 * 'use client' is load-bearing, not habit: every translation in this app is
 * resolved at runtime through LanguageContext, so bilingual copy cannot be
 * rendered from a server component.
 *
 * No retry control. A missing page is permanent, and React Standards 11.3
 * reserves "try again" for retryable failures -- the very affordance #1429
 * called out as one "that can never work".
 */
export default function NotFound() {
  const { t } = useLanguage();

  return (
    <div className="max-w-2xl mx-auto px-3 md:px-6 py-16">
      <div
        data-testid="not-found-card"
        className="rounded-2xl border border-border bg-background-secondary p-8 text-center shadow-sm space-y-4"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Compass size={24} aria-hidden="true" />
        </div>
        <h1 data-testid="not-found-title" className="text-2xl font-semibold text-foreground">
          {t.notFoundPage.title}
        </h1>
        <p data-testid="not-found-description" className="text-sm text-foreground-muted">
          {t.notFoundPage.description}
        </p>

        <div className="flex flex-col sm:flex-row sm:justify-center gap-3 pt-2">
          <Link
            href="/"
            data-testid="not-found-home-link"
            className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-white hover:opacity-90 transition"
          >
            {t.notFoundPage.goHome}
          </Link>
          <Link
            href="/events"
            data-testid="not-found-events-link"
            className="inline-flex items-center justify-center rounded-md border border-border px-5 py-2.5 text-sm font-medium text-foreground hover:bg-background transition"
          >
            {t.eventDetail.backToEvents}
          </Link>
        </div>
      </div>
    </div>
  );
}
